import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button, SpinnerOverlay, validationErrMessages } from '@components/common';
import FormWithTabs from '@components/common/FormWithTabs';

import { useQueryAlarmUsers } from '@hooks/alarms/useQueryAlarmUsers';
import { useQueryOranUserGroupsForAlarms } from '@hooks/alarms/useQueryOranUserGroupsForAlarms';
import { useQueryUserGroups } from '@hooks/alarms/useQueryUserGroups';
import { useQueryUser } from '@hooks/useQueryUser';

import { IAlarmManagement } from '@interfaces/alarms/alarm';
import { IAlarmSettingsDTO } from '@interfaces/alarms/dto/alarmsDTO';
import { IValueLabelColor } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ITabForm } from '@interfaces/components/form';

import { TFrequency, TRenotify } from '@enums/alarms';
import { EVendorsValues, EYupErrorTypes, TUserRoles } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';
import { EAlarmManagementFields } from '@components/ModalComponents/AlarmManagementForm/enums';

import { tabsNames } from '@components/ModalComponents/AlarmManagementForm/constants';

import CustomAlarm from './CustomAlarm/CustomAlarm';
import BasicInformation from './BasicInformation';
import Channel from './Channel';
import Schedule from './Schedule';
import { schema } from './schema';
import { prepareDefaultValues } from './utils';

import styles from './AlarmManagementForm.module.scss';

export interface INetworkElementFields {
	networkElement: TSelectOption<string>;
	minutes: TSelectOption<string>;
	ruleId?: string;
	id: string;
}

export interface IFieldsAlarmManagementForm {
	[EAlarmManagementFields.isActive]: boolean;
	[EAlarmManagementFields.frequency]: TFrequency | null;
	[EAlarmManagementFields.numberOfRepetitions]: number | null;
	[EAlarmManagementFields.repetitionHours]: TSelectOption<number>;
	[EAlarmManagementFields.repetitionMinutes]: TSelectOption<number>;
	[EAlarmManagementFields.renotify]: TRenotify | null;
	[EAlarmManagementFields.renotifyHours]: TSelectOption<number>;
	[EAlarmManagementFields.renotifyMinutes]: TSelectOption<number>;
	[EAlarmManagementFields.isNotification]: boolean;
	[EAlarmManagementFields.isEmail]: boolean;
	[EAlarmManagementFields.roles]: TSelectOption<TUserRoles>[] | null;
	[EAlarmManagementFields.users]: TSelectOption<string>[] | null;
	[EAlarmManagementFields.networkElementFields]: INetworkElementFields[];
	[EAlarmManagementFields.autoClearMinutes]: TSelectOption<number> | null;
}

export interface IAlarmManagementFormProps {
	editableAlarm?: IAlarmManagement;
	onClose: () => void;
	setIsDirtyFormModal: Dispatch<SetStateAction<boolean>>;
	editAlarm: (alarmSettings: IAlarmSettingsDTO) => void;
	isLoadingEditAlarm: boolean;
	fields: IValueLabelColor[];
	isCustomAlarm?: boolean;
}

const AlarmManagementForm = ({
	editableAlarm,
	isLoadingEditAlarm,
	fields,
	isCustomAlarm,
	editAlarm,
	onClose,
	setIsDirtyFormModal,
}: IAlarmManagementFormProps) => {
	const { rolesOptions, isLoadingRoles } = useQueryUserGroups();
	const { usersOptions, isLoadingUsers } = useQueryAlarmUsers();
	const { oranUserGroupsOptions, isLoadingOranUserGroupsOptions } = useQueryOranUserGroupsForAlarms();
	const { user } = useQueryUser();

	const isOranVendor = user
		?.organization
		?.availableVendors
		.some(({ name }) => name === EVendorsValues.oran);

	const isLoading = (
		isLoadingRoles ||
		isLoadingUsers ||
		isLoadingEditAlarm ||
		isLoadingOranUserGroupsOptions
	);

	const defaultValues = useMemo(() => {
		return prepareDefaultValues({
			editableAlarm,
			rolesOptions,
			usersOptions,
		});
	}, [editableAlarm, rolesOptions, usersOptions]);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		control,
		setValue,
		watch,
		trigger,
		clearErrors,
		reset,
		setError,
	} = useForm<IFieldsAlarmManagementForm>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema(!!editableAlarm?.autoClearMinutes)),
		defaultValues,
	});

	const { fields: networkElementFields, append, remove } = useFieldArray<any>({
		control,
		name: EAlarmManagementFields.networkElementFields,
	});

	useEffect(() => {
		if (!editableAlarm?.id) return;

		reset(defaultValues);
		// eslint-disable-next-line
	}, [
		editableAlarm?.id,
		rolesOptions.length,
		usersOptions.length,
	]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const setErrorsChannel = useCallback(() => {
		setError(EAlarmManagementFields.isEmail, {
			type: EYupErrorTypes.required,
			message: validationErrMessages.channel,
		});
		setError(EAlarmManagementFields.isNotification, {
			type: EYupErrorTypes.required,
			message: validationErrMessages.channel,
		});
	}, [setError]);

	const setErrorsRecipients = useCallback(() => {
		setError(EAlarmManagementFields.users, {
			type: EYupErrorTypes.required,
			message: validationErrMessages.recipient,
		});
		setError(EAlarmManagementFields.roles, {
			type: EYupErrorTypes.required,
			message: validationErrMessages.recipient,
		});
	}, [setError]);

	const clearErrorsChannel = useCallback(() => {
		clearErrors([EAlarmManagementFields.isEmail, EAlarmManagementFields.isNotification]);
	}, [clearErrors]);

	const clearErrorsRecipients = useCallback(() => {
		clearErrors([EAlarmManagementFields.users, EAlarmManagementFields.roles]);
	}, [clearErrors]);

	const checkIsValidAdditionalCheck = useCallback(() => {
		const isEmail = watch(EAlarmManagementFields.isEmail);
		const isNotification = watch(EAlarmManagementFields.isNotification);
		const users = watch(EAlarmManagementFields.users);
		const roles = watch(EAlarmManagementFields.roles);

		if (!isEmail && !isNotification) setErrorsChannel();
		else clearErrorsChannel();

		if (!users?.length && !roles?.length) setErrorsRecipients();
		else clearErrorsRecipients();

		return (isEmail || isNotification) && (!!users?.length || !!roles?.length);
	}, [clearErrorsChannel, clearErrorsRecipients, setErrorsChannel, setErrorsRecipients, watch]);

	const isChannelError = !!(errors?.isEmail || errors?.isNotification || errors?.roles || errors?.users);

	const isScheduleError = !!(errors?.frequency || errors?.numberOfRepetitions || errors?.repetitionHours
		|| errors?.repetitionMinutes || errors?.renotify || errors?.renotifyHours || errors?.renotifyMinutes);

	const isCustomAlarmError = !!(errors?.networkElementFields);

	const tabs: Array<ITabForm> =
		[
			{
				title: 'Basic information',
				component: BasicInformation,
				value: tabsNames.basicInfo,
				isTabError: !!errors?.isActive,
				props: {
					watch,
					setValue,
					editableAlarm,
					fields,
					control,
				},
			},
			{
				title: 'Schedule',
				component: Schedule,
				value: tabsNames.schedule,
				isTabError: isScheduleError,
				props: {
					watch,
					control,
					register,
					errors,
				},
			},
			{
				title: 'Channel',
				component: Channel,
				value: tabsNames.channel,
				isTabError: isChannelError,
				props: {
					watch,
					setValue,
					errors,
					setErrorsChannel,
					clearErrorsChannel,
					rolesOptions: isOranVendor ? oranUserGroupsOptions : rolesOptions,
					usersOptions,
					setErrorsRecipients,
					clearErrorsRecipients,
					control,
				},
			},
			{
				title: 'AWTG RAN Alarm Settings',
				component: CustomAlarm,
				value: tabsNames.customAlarm,
				isTabError: isCustomAlarmError,
				isHidden: !isCustomAlarm,
				props: {
					watch,
					setValue,
					errors,
					control,
					setErrorsChannel,
					clearErrorsChannel,
					setErrorsRecipients,
					clearErrorsRecipients,
					networkElementFields,
					editableAlarm,
					append,
					remove,
				},
			},
		];

	const onSubmit = (values: IFieldsAlarmManagementForm) => {
		if (!editableAlarm) return;

		const alarmSettings: IAlarmSettingsDTO = {
			id: editableAlarm.id,
			isEnabled: values.isActive,
			frequency: values.frequency,
			numberOfRepetitions: values.numberOfRepetitions,
			repetitionHours: +(values.repetitionHours?.value || 0),
			repetitionMinutes: +(values.repetitionMinutes?.value || 0),
			renotify: values.renotify,
			renotifyHours: +(values.renotifyHours?.value || 0),
			renotifyMinutes: +(values.renotifyMinutes?.value || 0),
			isNotification: values.isNotification,
			isEmail: values.isEmail,
			roles: values.roles?.map((option: TSelectOption<TUserRoles>) => option?.value as TUserRoles) || null,
			users: values.users?.map((option) => option?.value as string) || null,
			autoClearMinutes: values.autoClearMinutes?.value || null,
			networkElementFields: values.networkElementFields?.map((alarm: INetworkElementFields) => ({
				id: alarm?.ruleId,
				alarmDbId: editableAlarm.id,
				networkElementId: alarm.networkElement?.value,
				noEventMinutes: alarm.minutes?.value,
			})) || null,
		};

		editAlarm(alarmSettings);
	};

	const preSubmit = async () => {
		const isValid = await trigger();
		const isValidAdditionalCheck = checkIsValidAdditionalCheck();

		if (isValid && isValidAdditionalCheck) await handleSubmit(onSubmit)();
	};

	return (
		<div className={styles.alarmManagementForm}>
			{isLoading && <SpinnerOverlay />}
			<FormWithTabs
				isEditMode
				tabs={tabs}
			/>
			<div className={styles.buttons}>
				<Button
					onClick={onClose}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={preSubmit}
					text='Save'
				/>
			</div>
		</div>
	);
};

export default AlarmManagementForm;
